import React, { useState, useEffect } from "react";
import { getTaskForecast, recurringList, recurringFromTask, recurringSetActive, recurringSave } from "../../../../api/hub/work";
import TaskHeader from "../list/TaskHeader";
import PipelinePositions from "./PipelinePositions";
import ConversationThread from "./ConversationThread";
import SubtaskSection from "../list/SubtaskSection";
import CommitmentList from "./CommitmentList";
import AttachmentList from "./AttachmentList";
import Composer from "../../kit/Composer";
import { useToast } from "../../../../contexts/ToastContext";

// ─── Icons (inline SVG; lucide-react is not a project dependency) ───────────────
// Paths mirror lucide-react's ChevronRight / ChevronDown / CircleDot glyphs so
// the visual matches the tracker spec (Item 5) without adding a new dependency.
const ChevronRightIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);
const ChevronDownIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const CircleDotIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="1" />
  </svg>
);

// ─── Repeat cadence vocabulary (work-page-fixes Item 1) ──────────────────────
// Mirrors CADENCE_OPTIONS / WEEKDAY_OPTIONS in RepeatingView.js so the slider's
// inline form presents the same schedule options as the Repeating tab. Hoisted
// to module scope so the arrays are NOT reallocated on every render.
const CADENCE_OPTIONS = [
  { value: "daily", label: "Every day" },
  { value: "weekly", label: "Every week" },
  { value: "biweekly", label: "Every 2 weeks" },
  { value: "monthly", label: "Every month" },
  { value: "quarterly", label: "Every 3 months" },
  { value: "yearly", label: "Every year" },
];

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" }, { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" }, { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" }, { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

// Cadences that need a day-of-month picker (1–31). Weekly uses a weekday
// picker; daily/biweekly/yearly have no "On" field at all.
const DAY_OF_MONTH_CADENCES = new Set(["monthly", "quarterly"]);

// The list RPC names the active flag `is_active`; some rule shapes also carry
// `isActive`. Accept either so a backend casing change doesn't silently flip
// the toggle. Mirrors isRuleActive in RepeatingView.js.
const isRuleActive = (r) => !!(r && (r.is_active ?? r.isActive));


// ─── WorkPanel ──────────────────────────────────────────────────────────────────
// Orchestrator. The task with its full conversation and a composer.
// "Not a preview. Not a details pane." (spec §2).
// Replaces WorkPanelPlaceholder — same props, same data shape.
//
// <Composer> is wired below — writes through fn_compose_send via kit/Composer.
// When Charles ships <Timeline>, ConversationThread can be swapped too.
const WorkPanel = ({ detail, taskId, recurring, onClose, onClientClick, onTaskUpdated, onPositionClick, onTitleChange, onDueDateChange, onAssigneeClick, onDescriptionChange, showError, showSuccess, onPriorityChange, priorityOptions, onServiceChange, serviceOptions, subtaskRevision, workTypeOptions = [], onWorkTypeChange, onClientChange, onDelete }) => {
  // ─── Forecast (fn_forecast_task) ───────────────────────────────────────────
  // The dedicated RPC returns the full picture (times_typical.median, past_p90,
  // observations, basis) — richer than the ai_forecast_at fields on the task.
  // Hooks run before the `if (!detail)` skeleton early-return so they're called
  // unconditionally on every render (rules of hooks).
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(true);
  // ─── Dependencies collapse (Item 5) — collapsed by default ──────────────────
  // Toggled by the "Dependencies (N)" header. Declared with the other hooks
  // before the `if (!detail)` early-return so it's always called.
  const [depsExpanded, setDepsExpanded] = useState(false);

  // ─── Delete task (Item 3) — inline confirm-on-second-click ──────────────
  // Declared before the `if (!detail)` early-return so the hook always runs.
  // First click arms the confirm; second click calls fn_work_delete via
  // onDelete. fn_work_delete is a soft delete that REFUSES when effort is
  // logged or an open commitment exists — see handleConfirmDelete below.
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ─── Repeat schedule (work-page-fixes Item 1) ──────────────────────────────
  // recurringRule holds the recurring rule whose open_copy.id matches this task
  // (null when the task doesn't repeat). repeatFormOpen drives the inline
  // cadence/day form; repeatCadence / repeatDayOfWeek / repeatDayOfMonth are the
  // form fields; repeatSaving guards the Set + toggle buttons during RPC calls.
  // Declared before the `if (!detail)` early-return so the hooks always run.
  const [recurringRule, setRecurringRule] = useState(null);
  const [repeatFormOpen, setRepeatFormOpen] = useState(false);
  const [repeatCadence, setRepeatCadence] = useState("weekly");
  const [repeatDayOfWeek, setRepeatDayOfWeek] = useState(1);
  const [repeatDayOfMonth, setRepeatDayOfMonth] = useState(1);
  const [repeatSaving, setRepeatSaving] = useState(false);

  // ─── Repeat-toggle toast (work-page-fixes Item 2) ───────────────────────────
  // The slide-out uses the bottom-center ToastContext toast (same as
  // TaskHeader's "Link copied" and useWorkDetail's inline-edit toasts), NOT the
  // NotificationContext modal that the showSuccess/showError props render.
  // handleRepeatToggle fires toastSuccess("Repeating paused"|"resumed");
  // toastError covers the recurringSetActive failure path.
  const { toastSuccess, toastError } = useToast();

  useEffect(() => {
    if (!taskId) { setForecast(null); setForecastLoading(true); return; }
    let cancelled = false;
    setForecastLoading(true);
    getTaskForecast(taskId)
      .then((resp) => {
        if (!cancelled) { setForecast(resp?.data ?? null); setForecastLoading(false); }
      })
      .catch(() => {
        if (!cancelled) { setForecast(null); setForecastLoading(false); }
      });
    return () => { cancelled = true; };
  }, [taskId]);

  // ─── Repeat schedule fetch (work-page-fixes Item 1) ────────────────────────
  // fn_work_detail doesn't return recurring schedule info, so fetch every
  // recurring rule via recurringList() and find the one whose open_copy.id
  // matches this task. String() coercion on both sides guards type mismatches
  // (uuid vs string). Runs before the `if (!detail)` early-return so the hook
  // is always called. Reset to null when the task changes or on error.
  useEffect(() => {
    if (!detail?.task?.id) { setRecurringRule(null); return; }
    let cancelled = false;
    recurringList()
      .then((resp) => {
        if (cancelled) return;
        const list = Array.isArray(resp?.data) ? resp.data : [];
        const match = list.find((r) => String(r?.open_copy?.id) === String(detail.task.id)) || null;
        setRecurringRule(match);
      })
      .catch(() => { if (!cancelled) setRecurringRule(null); });
    return () => { cancelled = true; };
  }, [detail?.task?.id]);

  if (!detail) {
    // Loading skeleton — same shape as the page-level WorkPanelSkeleton
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-6 animate-pulse">
        <div className="h-5 w-64 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
        <div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded" />
          ))}
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
          <div className="h-10 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
      </div>
    );
  }

  // fn_work_detail does not return `recurring`, so the parent passes it down
  // from the fn_work_list list item (work-page-fixes §A Item 1). Merge it onto
  // the task object so TaskHeader's existing recurring badge renders. Only
  // override when `recurring` is a defined boolean (true/false); when it's
  // undefined (deep link before any list fetch) fall back to whatever the
  // detail response carries, if anything.
  const task = recurring != null ? { ...(detail.task || {}), recurring } : (detail.task || {});
  const positions = detail.positions || [];
  const messages = detail.messages || [];
  const commitments = detail.commitments || [];
  const attachments = detail.attachments || [];
  const effort = detail.effort;

  // ─── Dependencies (Item 5) — filter orphaned ClickUp refs ───────────────────
  // fn_work_detail returns a dependencies[] array; some entries are orphaned
  // ClickUp references with no resolvable task id. Those are dropped and never
  // rendered. The verified response shape uses `id` / `title` / `status`, while
  // the spec names `other_task_id` / `other_title` — normalise by falling back
  // so the section keeps working across either shape. realDeps holds only
  // entries with a non-null resolvable task id.
  const realDeps = (Array.isArray(detail.dependencies) ? detail.dependencies : [])
    .filter((dep) => {
      const depId = dep.other_task_id != null ? dep.other_task_id : dep.id;
      return depId != null;
    });

  // One-level nesting rule (spec §5): the subtask section only appears
  // for top-level tasks. If this task itself has a parent_task_id, it IS a
  // subtask and must not be able to create sub-subtasks.
  const isTopLevel = !task.parent_task_id;

  // SubtaskSection owns its local array — the list updates immediately on
  // create/delete/rename. This callback signals the parent (WorkPage) to
  // refresh the list-level subtasks count aggregate (the badge) via
  // onTaskUpdated, which bumps retryNonce and re-fetches the bucket cache.
  // A more surgical patchItem(taskId, { subtasks: newCount }) wiring will
  // replace this when WorkPage.js is updated in a follow-up step.
  const handleSubtaskCountChange = () => {
    onTaskUpdated?.();
  };

  // ─── Delete task (Item 3) — second click runs fn_work_delete via onDelete.
  // fn_work_delete refuses (raises) when effort is logged or an open
  // commitment exists; onDelete returns false on refusal, so we keep the
  // slide-out open and reset the armed state. On success onDelete closes it.
  const handleConfirmDelete = async () => {
    try {
      const ok = await onDelete?.(task.id || taskId);
      setConfirmDelete(false);
      if (ok !== false) onClose?.();
    } catch {
      setConfirmDelete(false);
    }
  };

  // ─── Repeat handlers (work-page-fixes Item 1) ─────────────────────────────
  // repeatToggleOn reflects the rule's active state — on when the rule exists
  // AND is active, off when null (no rule) OR paused. Clicking the label always
  // opens the inline form: prefilled from the existing rule (edit) or defaulted
  // from the task's due date (create). Clicking the toggle on a non-repeating
  // task also opens the create form; on a repeating task it pauses/resumes via
  // recurringSetActive. The Set button branches: recurringFromTask for a new
  // rule, recurringSave (preserving the rule's other fields) for an edit.
  const repeatToggleOn = !!(recurringRule && isRuleActive(recurringRule));

  const openRepeatForm = () => {
    const dueDate = task.due ? new Date(task.due) : null;
    const valid = dueDate && !isNaN(dueDate.getTime());
    setRepeatCadence(recurringRule?.cadence || "weekly");
    setRepeatDayOfWeek(recurringRule?.day_of_week ?? (valid ? dueDate.getDay() : 1));
    setRepeatDayOfMonth(recurringRule?.day_of_month ?? (valid ? dueDate.getDate() : 1));
    setRepeatFormOpen(true);
  };

  const handleRepeatLabelClick = () => {
    if (repeatSaving) return;
    openRepeatForm();
  };

  const handleRepeatToggle = async () => {
    if (repeatSaving) return;
    if (!recurringRule) {
      // No rule yet — open the create form (same as clicking the label).
      openRepeatForm();
      return;
    }
    // Rule exists — flip its active flag (pause/resume) and update locally.
    const next = !isRuleActive(recurringRule);
    setRepeatSaving(true);
    try {
      const resp = await recurringSetActive(recurringRule.id, next);
      const updated = resp?.data ?? null;
      setRecurringRule(updated || { ...recurringRule, is_active: next });
      toastSuccess(next ? "Repeating resumed" : "Repeating paused");
    } catch (e) {
      toastError(e?.message || "Could not update repeat");
    } finally {
      setRepeatSaving(false);
    }
  };

  // ─── Repeat inline-edit commit handlers (work-page-fixes Item 1) ──────────────
  // The slider's inline pattern: every selection commits immediately (no Set
  // button). handleRepeatCadenceChange / handleRepeatDayOfWeekChange /
  // handleRepeatDayOfMonthChange each build the rule payload (preserving the
  // rule's other fields for an edit, defaulting the cadence-specific day from
  // the task due date for a create) and call recurringSave (edit) or
  // recurringFromTask (create), then showSuccess and collapse the inline
  // controls back to the schedule label. repeatScheduleSentence formats the
  // toast (e.g. "Repeat updated to every month on the 18th").
  const repeatScheduleSentence = (cadence, dow, dom) => {
    const c = CADENCE_OPTIONS.find((o) => o.value === cadence);
    const base = c ? c.label : "Repeat";
    if (cadence === "weekly") {
      const w = WEEKDAY_OPTIONS.find((o) => o.value === dow);
      return `${base} on ${w ? w.label : "Monday"}`;
    }
    if (DAY_OF_MONTH_CADENCES.has(cadence)) {
      return `${base} on the ${dom ?? 1}`;
    }
    return base;
  };

  const commitRepeat = async (cadence, dow, dom) => {
    if (repeatSaving) return;
    setRepeatSaving(true);
    try {
      if (recurringRule) {
        // Edit existing rule — preserve its fields, override cadence + day.
        const payload = {
          ...recurringRule,
          cadence,
          day_of_week: cadence === "weekly" ? dow : null,
          day_of_month: DAY_OF_MONTH_CADENCES.has(cadence) ? dom : null,
        };
        delete payload.open_copy;
        const resp = await recurringSave(payload);
        const saved = resp?.data ?? null;
        setRecurringRule(saved || {
          ...recurringRule,
          cadence,
          day_of_week: cadence === "weekly" ? dow : null,
          day_of_month: DAY_OF_MONTH_CADENCES.has(cadence) ? dom : null,
        });
        toastSuccess(`Repeat updated to ${repeatScheduleSentence(cadence, dow, dom)}`);
      } else {
        // Create a new repeat from this task.
        await recurringFromTask(
          detail.task.id,
          cadence,
          cadence === "weekly" ? dow : null,
          DAY_OF_MONTH_CADENCES.has(cadence) ? dom : null,
        );
        toastSuccess(`Repeat set to ${repeatScheduleSentence(cadence, dow, dom)}`);
        // Re-fetch so the row shows the real schedule_label from the backend.
        const list = await recurringList();
        const rules = Array.isArray(list?.data) ? list.data : [];
        const match = rules.find((r) => String(r?.open_copy?.id) === String(detail.task.id)) || null;
        setRecurringRule(match);
      }
      setRepeatFormOpen(false);
    } catch (e) {
      // The backend raises user-facing messages (e.g. "this task already
      // repeats") — show verbatim via toastError (bottom-center toast, not the
      // NotificationContext modal that showError renders).
      toastError(e?.message || "Could not update repeat");
    } finally {
      setRepeatSaving(false);
    }
  };

  // Cadence change — default the cadence-specific day from the existing rule or
  // the task due date, then commit immediately.
  const handleRepeatCadenceChange = (nextCadence) => {
    const dueDate = task.due ? new Date(task.due) : null;
    const valid = dueDate && !isNaN(dueDate.getTime());
    const dow = nextCadence === "weekly" ? (recurringRule?.day_of_week ?? (valid ? dueDate.getDay() : 1)) : null;
    const dom = DAY_OF_MONTH_CADENCES.has(nextCadence) ? (recurringRule?.day_of_month ?? (valid ? dueDate.getDate() : 1)) : null;
    setRepeatCadence(nextCadence);
    commitRepeat(nextCadence, dow, dom);
  };

  // Day-of-week change (weekly only) — keep the cadence, commit the new weekday.
  const handleRepeatDayOfWeekChange = (nextDow) => {
    const cadence = recurringRule?.cadence || "weekly";
    setRepeatDayOfWeek(nextDow);
    commitRepeat(cadence, nextDow, null);
  };

  // Day-of-month change (monthly/quarterly) — keep the cadence, commit the new day.
  const handleRepeatDayOfMonthChange = (nextDom) => {
    const cadence = recurringRule?.cadence || "monthly";
    setRepeatDayOfMonth(nextDom);
    commitRepeat(cadence, null, nextDom);
  };

  const handleRepeatCancel = () => {
    setRepeatFormOpen(false);
  };

  return (
    <div className="flex flex-col md:flex-row h-full overflow-x-hidden">
      {/* ─── Close button (always visible) ─────────────────────────────── */}
      <div className="absolute top-3 right-3 z-10">
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-lg bg-white/80 dark:bg-gray-900/80 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shadow-sm"
          aria-label="Close work panel"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* ─── Left column (details) ─────────────────────────────────────── */}
      <div key={`work-left-${taskId}`} className="flex-1 min-w-0 md:overflow-y-auto p-6">

      {/* ─── Task header ──────────────────────────────────────────────── */}
      <TaskHeader
        task={task}
        taskId={taskId}
        forecast={forecast}
        onClientClick={onClientClick}
        onTaskUpdated={onTaskUpdated}
        onTitleChange={onTitleChange}
        onDueDateChange={onDueDateChange}
        onAssigneeClick={onAssigneeClick}
        onDescriptionChange={onDescriptionChange}
        showError={showError}
        showSuccess={showSuccess}
        onPriorityChange={onPriorityChange}
        priorityOptions={priorityOptions}
        onServiceChange={onServiceChange}
        serviceOptions={serviceOptions}
        positions={positions}
        onPositionClick={onPositionClick}
        workTypeOptions={workTypeOptions}
        onWorkTypeChange={onWorkTypeChange}
        onClientChange={onClientChange}
        repeat={{
          rule: recurringRule,
          open: repeatFormOpen,
          toggleOn: repeatToggleOn,
          saving: repeatSaving,
          cadence: repeatCadence,
          dayOfWeek: repeatDayOfWeek,
          dayOfMonth: repeatDayOfMonth,
          onLabelClick: handleRepeatLabelClick,
          onToggle: handleRepeatToggle,
          onCadenceChange: handleRepeatCadenceChange,
          onDayOfWeekChange: handleRepeatDayOfWeekChange,
          onDayOfMonthChange: handleRepeatDayOfMonthChange,
          onClose: handleRepeatCancel,
        }}
      />

      {/* ─── Status pipeline (moved below assignee/due — Item 1) ─────── */}
      {/* Renders after TaskHeader (which owns the title, client, assignee/due
          row, and the work-type/priority/service pills) and before the content
          sections (client visibility, subtasks, commitments, etc.). Compact
          text-only pills in a flex-wrap row — see PipelinePositions.js. */}
      <PipelinePositions
        positions={positions}
        currentStatus={task.status}
        onPositionClick={onPositionClick}
      />



      {/* ─── Client visibility indicator (true only — absence is default) ── */}
      {task.is_client_visible && (
        <div className="flex items-center gap-1 mb-3 text-xs text-green-600 dark:text-green-400">
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          Visible to client
        </div>
      )}

      {/* ─── Subtasks (top-level tasks only) ─────────────────────────── */}
      {isTopLevel && <SubtaskSection revision={subtaskRevision} parentId={task.id} onCountChange={handleSubtaskCountChange} />}

      {/* ─── Commitments ──────────────────────────────────────────────── */}
      <CommitmentList commitments={commitments} />

      {/* ─── Dependencies (Item 5 — collapsible, orphaned refs filtered) ─── */}
      {realDeps.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <button
            type="button"
            onClick={() => setDepsExpanded((v) => !v)}
            className="flex items-center gap-1 text-sm font-medium text-neutral-300 cursor-pointer"
          >
            {depsExpanded ? (
              <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
            )}
            Dependencies ({realDeps.length})
          </button>
          {depsExpanded && (
            <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5">
              {realDeps.map((dep, i) => {
                const depId = dep.other_task_id != null ? dep.other_task_id : dep.id;
                const depTitle = dep.other_title != null ? dep.other_title : dep.title;
                const label = depTitle || (depId != null ? `#${depId}` : "Dependency");
                const clickable = depId != null;
                return (
                  <div
                    key={depId || i}
                    className={`flex items-start gap-2 px-2 py-1.5 rounded ${
                      clickable ? "cursor-pointer hover:text-white" : ""
                    }`}
                    onClick={clickable ? () => {
                      // TODO: open task slide-out for this dependency
                    } : undefined}
                  >
                    <CircleDotIcon className="w-3 h-3 text-neutral-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-300">{label}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ─── Attachments ──────────────────────────────────────────────── */}
      <AttachmentList attachments={attachments} taskId={taskId} onTaskUpdated={onTaskUpdated} showError={showError} />

      {/* ─── Effort ───────────────────────────────────────────────────── */}
      {effort && (
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Effort</p>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {effort.summary || "Logged"}
          </p>
        </div>
      )}

      {/* Past similar — renders only when embeddings land */}
      {detail.past_similar?.length > 0 && (
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-800">
          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
            Similar completed tasks ({detail.past_similar.length})
          </p>
          <div className="space-y-1.5">
            {detail.past_similar.map((item, i) => (
              <div key={item.id || i} className="flex items-center gap-2 py-1">
                <svg className="w-4 h-4 text-green-500 flex-shrink-0" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  {item.title || "Untitled"}
                </span>
                {item.completed_at && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">
                    {new Date(item.completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Delete task (inline confirm-on-second-click — Item 3) ──────── */}
      <button
        type="button"
        onClick={confirmDelete ? handleConfirmDelete : () => setConfirmDelete(true)}
        className={confirmDelete
          ? "text-xs text-red-600 dark:text-red-400 font-medium cursor-pointer mt-4 text-left"
          : "text-xs text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 cursor-pointer mt-4 text-left"}
      >
        {confirmDelete ? "Confirm delete?" : "Delete task"}
      </button>

      </div>

      {/* ─── Right column (conversation) ───────────────────────────────── */}
      {/* h-full on desktop (side-by-side column fills the slideout height so the
          composer pins to the bottom). On mobile (stacked) h-full would force the
          conversation column to the full viewport height; with no messages the
          empty area collapses and leaves a huge void below the composer. So on
          mobile when empty, use h-auto so the column shrinks to its content
          (header + "No messages" + composer) with no void. When messages exist
          on mobile, keep h-full so the thread scrolls and the composer pins. */}
      <div key={`work-right-${taskId}`} className={`w-full md:w-[420px] shrink-0 border-l border-gray-200 dark:border-gray-700 flex flex-col ${messages.length === 0 ? "h-auto md:h-full" : "h-full"} md:overflow-hidden`}>
        {/* Conversation header */}
        <div className="px-6 py-2">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Conversation
            {messages.length > 0 && (
              <span className="ml-1.5 font-normal text-gray-400 dark:text-gray-500">
                {messages.length} message{messages.length !== 1 ? "s" : ""}
              </span>
            )}
          </h4>
        </div>

        {/* Conversation thread (own scroll) */}
        {/* flex-1 fills available space so the composer pins to the bottom.
            On mobile, when there are no messages, drop flex-1 so the empty
            "No messages yet" block collapses to its own height and the composer
            sits close below it (Item 3). On desktop md:flex-1 keeps the fill. */}
        <div className={`${messages.length === 0 ? "md:flex-1" : "flex-1"} md:overflow-y-auto min-h-0`}>
          <ConversationThread messages={messages} threadId={detail.thread_id} />
        </div>

        {/* Composer (pinned at bottom) */}
        {/* id="conversation-composer" — share "Send message" scrolls here (Item 2) */}
        <div id="conversation-composer" className="p-4 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <Composer
            threadId={detail.thread_id || detail.task?.thread_id}
            taskId={detail.id || detail.task?.id || taskId}
            clientId={detail.client_id || detail.task?.client_id}
            onSent={onTaskUpdated}
          />
        </div>
      </div>
    </div>
  );
};

export default WorkPanel;
